/* eslint-disable custom/no-restricted-dayjs */
import * as React from 'react';

import { FavoriteTwoTone } from '@mui/icons-material';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import { Button, IconButton, Typography } from '@mui/material';

import dayjs from 'dayjs';

import { track } from '@hbf/analytics';

import { AnalyticsEvent } from '@kamernet/core/Analytics/types';
import { AppServicesContext } from '@kamernet/core/AppServices';
import { AuthContext } from '@kamernet/core/Auth/AuthContext';
import { AuthSource, AuthAction } from '@kamernet/core/Auth/types';
import { useAuthenticationRouting } from '@kamernet/core/Auth/useAuthenticationRouting';
import { useIntl } from '@kamernet/core/Intl';
import { logger } from '@kamernet/core/Logger';
import { NavLink } from '@kamernet/core/Routing/NavLink';
import { ROUTES } from '@kamernet/core/Routing/routes';
import { useMediaIsDesktop } from '@kamernet/utilities/Media/useMediaIsDesktop';

import {
  FurnishingLabelTranslations,
  RoomTypeLabelTranslations,
} from '@kamernet/modules/Listing/translations';

import { ListingDetailsContext } from '../../ListingDetailsContext';
import { SocialMediaShare } from '../SocialMediaShare';

import { Breadcrumbs } from './components/Breadcrumbs/Breadcrumbs';
import styles from './Header.module.css';

export const Header = () => {
  const { listingDetails, shareTargetURL } = ListingDetailsContext.useContext();
  const { isAuthenticated } = AuthContext.useContext();
  const [isFavoriteListing, setIsFavorite] = React.useState(
    listingDetails.isFavorite,
  );
  const { navigateToAuthentication } = useAuthenticationRouting();
  const { apiClient } = AppServicesContext.useContext();
  const { T, locale } = useIntl();

  const furnishing = listingDetails.furnishingId
    ? T(FurnishingLabelTranslations[listingDetails.furnishingId])
    : '';

  const listingType = listingDetails.listingTypeId
    ? T(RoomTypeLabelTranslations[listingDetails.listingTypeId])
    : '';

  const isDesktop = useMediaIsDesktop();

  const getNumberOfDaysPosted = React.useCallback(
    (publishDateString: string) => {
      const now = dayjs();
      const publishDateDayjs = dayjs(publishDateString);

      if (publishDateDayjs.isSame(now)) {
        return 0;
      }

      let hours = now.diff(publishDateDayjs, 'hours');
      const days = Math.floor(hours / 24);
      hours -= days * 24;

      if (days === 0) {
        return T('ldp.posted_x_hours_ago', hours);
      }

      return T(
        'ldp.posted_more_than_1_days_ago',
        now.diff(publishDateDayjs, 'day'),
      );
    },
    [T],
  );

  const handleFavoriteClick = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isAuthenticated) {
        navigateToAuthentication({
          action: AuthAction.Signup,
          source: AuthSource.ListingFavouriting,
        });

        return;
      }
      if (!isFavoriteListing) {
        track(AnalyticsEvent.ListingFavorited);
      }

      setIsFavorite(!isFavoriteListing);

      try {
        await apiClient.favorite.addFavoriteListing({
          id: listingDetails.listingId,
        });
      } catch (error) {
        logger.error(
          `Listing Search Page - Failed to add listing as favorite: ${error}`,
        );
      }
    },
    [
      isAuthenticated,
      isFavoriteListing,
      navigateToAuthentication,
      apiClient.favorite,
      listingDetails.listingId,
    ],
  );

  return (
    <section className={styles.root}>
      <nav className={styles.breadcrumbs}>
        <Breadcrumbs />
      </nav>
      {!isDesktop && (
        <div className={styles.nav}>
          <NavLink.Anchor
            variant="body3Bold"
            href={ROUTES.ListingsSearchCity.getURL(
              {
                city: listingDetails.citySlug,
              },
              locale,
            )}
          >
            <Button
              className={styles.backButton}
              startIcon={<ArrowBackIosNewOutlinedIcon />}
            >
              {T('LBL_BACK')}
            </Button>
          </NavLink.Anchor>

          <div>
            <SocialMediaShare targetURL={shareTargetURL} />
            <IconButton
              className={`${styles.favorite} ${
                isFavoriteListing ? styles.filled : ''
              }`}
              onClick={handleFavoriteClick}
            >
              <FavoriteTwoTone />
            </IconButton>
          </div>
        </div>
      )}
      <Typography variant="h3">{listingDetails.computedStreetName}</Typography>
      {!isDesktop && (
        <div className={styles.locationDetails}>
          <NavLink.Native href="#map">
            {T(
              '%_comma_%',
              listingDetails.computedStreetName,
              listingDetails.computedCityName,
            )}
          </NavLink.Native>
        </div>
      )}
      <div className={styles.row}>
        <div className={styles.details}>
          <Typography variant="subtitle2">
            {T('lbl.%_%_for_rent', furnishing, listingType)}
          </Typography>

          <Typography color="mono.dark">•</Typography>
          {isDesktop && (
            <React.Fragment>
              <NavLink.Native variant="linkMedium" href="#map">
                {T(
                  '%_comma_%',
                  listingDetails.computedStreetName,
                  listingDetails.computedCityName,
                )}
              </NavLink.Native>
              <Typography color="mono.dark">•</Typography>
            </React.Fragment>
          )}
          {listingDetails.publishDate && (
            <Typography color="secondary.accent" variant="body3">
              {getNumberOfDaysPosted(listingDetails.publishDate)}
            </Typography>
          )}
        </div>

        {isDesktop && (
          <div>
            <SocialMediaShare targetURL={shareTargetURL} showLabel />

            <Button
              variant="text"
              className={`${styles.favorite} ${
                isFavoriteListing ? styles.filled : ''
              }`}
              startIcon={<FavoriteTwoTone />}
              onClick={handleFavoriteClick}
            >
              {isFavoriteListing
                ? T('ldp.lld.favourited')
                : T('ldp.label.favourite')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};
